// V12.5: utilitário de geocoding (Nominatim/OSM — grátis, sem chave) com FILA serializada
// que respeita o limite de ~1 req/seg da política de uso pública do Nominatim.
const NOMINATIM = "https://nominatim.openstreetmap.org";
const MIN_SPACING_MS = 1100;

let _chain = Promise.resolve();
let _lastTs = 0;

function _enqueue(fn) {
  const run = async () => {
    const wait = Math.max(0, MIN_SPACING_MS - (Date.now() - _lastTs));
    if (wait) await new Promise((r) => setTimeout(r, wait));
    _lastTs = Date.now();
    return fn();
  };
  const p = _chain.then(run, run);
  _chain = p.catch(() => {}); // mantém a fila viva mesmo se uma requisição falhar
  return p;
}

// V18.9.12 — fetch com timeout OBRIGATÓRIO: sem isso, uma requisição pendurada
// (rede instável no celular) travava a fila `_chain` para sempre e TODO geocoding
// do app parava de responder (mapa não centralizava, cidade não atualizava).
async function _fetchJson(url, ms = 9000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { headers: { Accept: "application/json" }, signal: ctrl.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(t);
  }
}

export function maskCEP(v) {
  return String(v || "").replace(/\D/g, "").slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");
}

function _parseAddress(a = {}) {
  return {
    street: a.road || a.pedestrian || a.path || "",
    neighborhood: a.suburb || a.neighbourhood || a.quarter || a.city_district || a.residential || "",
    city: a.city || a.town || a.village || a.municipality || "",
    uf: (a["ISO3166-2-lvl4"] || "").replace("BR-", "") || (a.state_code || "").toUpperCase() || "",
    cep: a.postcode ? maskCEP(a.postcode) : "",
    house_number: a.house_number || "",
  };
}

// Cidade/UF por um provedor alternativo (não passa pela fila do Nominatim).
// Usado quando o Nominatim falha — sem isso, um erro de rede no boot deixava o
// header travado na cidade padrão ("Sorriso").
async function _cityFromBigDataCloud(lat, lng) {
  const d = await _fetchJson(
    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=pt`,
  );
  const city = d.city || d.locality || "";
  const uf = (d.principalSubdivisionCode || "").replace("BR-", "");
  return city ? { city, uf } : null;
}

// Coordenadas → endereço
export async function reverseGeocode(lat, lng) {
  const primary = await _enqueue(async () => {
    try {
      const url = `${NOMINATIM}/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=pt-BR`;
      const d = await _fetchJson(url);
      return _parseAddress(d?.address || {});
    } catch (e) {
      console.warn("[geo] reverseGeocode (nominatim) falhou:", e?.message);
      return null;
    }
  });
  if (primary && primary.city) return primary;
  try {
    const alt = await _cityFromBigDataCloud(lat, lng);
    if (alt) return { ..._parseAddress(), ...(primary || {}), city: alt.city, uf: alt.uf || primary?.uf || "" };
  } catch (e) {
    console.warn("[geo] reverseGeocode (bigdatacloud) falhou:", e?.message);
  }
  return primary;
}

// Endereço (texto) → coordenadas + campos
export async function forwardGeocode(query) {
  const q = String(query || "").trim();
  if (!q) return null;
  return _enqueue(async () => {
    try {
      const url = `${NOMINATIM}/search?format=jsonv2&q=${encodeURIComponent(q)}&addressdetails=1&limit=1&countrycodes=br&accept-language=pt-BR`;
      const arr = await _fetchJson(url);
      if (!Array.isArray(arr) || arr.length === 0) return null;
      const r = arr[0];
      return { lat: parseFloat(r.lat), lng: parseFloat(r.lon), ..._parseAddress(r.address || {}) };
    } catch (e) {
      console.error("[geo] forwardGeocode failed", e?.message);
      return null;
    }
  });
}
